import { useState, useEffect } from 'react';
import * as React from 'react';
import classnames from 'classnames';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { debounce } from 'lodash';
import { ListingSearchCriteria } from '@components/layouts/Truva/ListingSearchDeprecated/utils';
import CustomIcon from '@components/shared/CustomIcon';
import Button from '@components/shared/V2Button';
import PossessionFilterList from '../Filters/PossessionFilter';
import CeilingHeightFilter from '../Filters/CeilingHeightFilter';
import SpaceConditionsFilter from '../Filters/SpaceConditionsFilter';
import TermsFilter from '../Filters/TermsFilter';
import SizeFilters from '../Filters/SizeFilter';
import PriceFilter from '../Filters/PriceFilter';
import FirstToMarketFilter from '../Filters/FirstToMarketFilter';
import LeaseTypeFilter from '../Filters/LeaseTypeFilter/LeaseTypeFilter';
import KeywordsFilter from '../Filters/KeywordsFilter/KeywordsFilter';
import AmenitiesFilter from '../Filters/AmenitiesFilter/AmenitiesFilter';
import SaveSearch from '../Filters/SaveSearch';
import FilterGroup from './FilterGroup';
import s from './Mobile.module.less';

const noOp = () => {};

const useAnimatedFlyout = () => {
  const [filterFlyoutOnScreen, setFilterFlyoutOnScreen] = React.useState(false);
  const [filterFlyoutHidden, setFilterFlyoutHidden] = React.useState(true);
  const openFlyout = async () => {
    setFilterFlyoutHidden(false);
    setTimeout(() => setFilterFlyoutOnScreen(true), 10);
  };
  const closeFlyout = () => {
    setFilterFlyoutOnScreen(false);
    setTimeout(() => setFilterFlyoutHidden(true), 500);
  };

  return {
    closeFlyout,
    openFlyout,
    filterFlyoutHidden,
    filterFlyoutOnScreen,
  };
};

const Mobile = () => {
  const { t } = useTranslation('filters');

  const { openFlyout, closeFlyout, filterFlyoutOnScreen, filterFlyoutHidden } = useAnimatedFlyout();

  // make a url builder & change it when the url changes
  // uses state instead of the useListingUrlBuilder, because
  // mobile filters don't change as you go, but
  const history = useHistory();
  const { marketSlug } = useParams<{ marketSlug: string }>();
  const { search: queryString } = useLocation();
  const [criteria, setUrlBuilder] = useState<ListingSearchCriteria>(
    ListingSearchCriteria.fromUrl({ queryString, marketSlug }),
  );
  useEffect(() => {
    setUrlBuilder(ListingSearchCriteria.fromUrl({ marketSlug, queryString }));
  }, [marketSlug, queryString]);

  const changeFilters = () => {
    closeFlyout();
    history.push(criteria.toUrl());
  };

  const onClose = () => {
    closeFlyout();
    setUrlBuilder(ListingSearchCriteria.fromUrl({ queryString, marketSlug }));
  };

  const clearFilters = () => {
    const sort = criteria.sort();
    setUrlBuilder(ListingSearchCriteria.fromUrl({ queryString: `?sort=${sort}`, marketSlug }));
  };

  const debouncedOnChange = debounce((callbackFn: () => ListingSearchCriteria) => {
    return callbackFn();
  }, 500);

  const onSizeChange = debounce((callbackFn: () => ListingSearchCriteria) => {
    const newCriteria = callbackFn();
    setUrlBuilder(newCriteria);
  }, 500);

  return (
    <>
      <div className={s.container}>
        <ul className={classnames(s.withSavedSearch, s.filters)}>
          <li>
            <button type="button" onClick={openFlyout}>
              <CustomIcon type="filters" />
              <span className={s.text}>
                {t(criteria.numFilters() === 0 ? 'header' : 'headerWithCount', {
                  count: criteria.numFilters(),
                })}
              </span>
            </button>
          </li>
          <li>
            <SaveSearch criteria={criteria} />
          </li>
        </ul>
        <div className={s.divider} />
      </div>
      <div
        data-testid="mobileFilterFlyout"
        className={classnames(
          s.filterFlyout,
          filterFlyoutOnScreen && s.onScreen,
          filterFlyoutHidden && s.hidden,
        )}
      >
        <div className={s.flyoutContainer}>
          <div className={s.header}>
            <button
              disabled={criteria.numFilters() < 1}
              className={s.button}
              type="button"
              onClick={clearFilters}
            >
              {t('clearFilters')}
            </button>
            <h3>{t('header')}</h3>
            <button className={s.button} type="button" onClick={onClose}>
              {t('common:cancel')}
            </button>
          </div>
          <ul className={s.filterList}>
            <li>
              <FirstToMarketFilter criteria={criteria} onChange={noOp} type="mobile" />
            </li>
            <FilterGroup title={t('price.title')}>
              <PriceFilter criteria={criteria} onChange={debouncedOnChange} type="mobile" />
            </FilterGroup>
            <FilterGroup title={t('size.title')}>
              <SizeFilters criteria={criteria} type="mobile" onChange={onSizeChange} />
            </FilterGroup>
            <FilterGroup title={t('spaceCondition.title')}>
              <SpaceConditionsFilter criteria={criteria} type="mobile" onChange={noOp} />
            </FilterGroup>
            <FilterGroup title={t('term.title')}>
              <TermsFilter onChange={noOp} criteria={criteria} type="mobile" />
            </FilterGroup>
            <FilterGroup title={t('possession.title')}>
              <PossessionFilterList onChange={noOp} criteria={criteria} type="mobile" />
            </FilterGroup>
            <FilterGroup title={t('ceilingHeight.title')}>
              <CeilingHeightFilter onChange={debouncedOnChange} criteria={criteria} type="mobile" />
            </FilterGroup>
            <FilterGroup title={t('leaseType.title')}>
              <LeaseTypeFilter onChange={noOp} criteria={criteria} type="mobile" />
            </FilterGroup>
            <FilterGroup title={t('keywords.title')}>
              <KeywordsFilter onChange={debouncedOnChange} criteria={criteria} type="mobile" />
            </FilterGroup>
            <FilterGroup title={t('amenitiesTitle')}>
              <AmenitiesFilter onChange={noOp} criteria={criteria} type="mobile" />
            </FilterGroup>
          </ul>
          <section className={s.footer}>
            <Button className={s.submit} type="primary" size="large" onClick={changeFilters}>
              {t('showResults')}
            </Button>
          </section>
        </div>
      </div>
    </>
  );
};

export default Mobile;
